import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { IFieldCondition } from '@fmnts/api/next';
import { Option, escapeRegExpString, isDefined, none } from '@fmnts/core';
import { distinctUntilArrayItemChanged } from '@fmnts/core/rxjs';
import {
  AbstractDeclarativeInputFieldComponent,
  ConditionalService,
  FormSelectField,
  SelectInputItem,
} from '@fmnts/declarative-forms';
import { bySelectItemValueEqeqeq } from '@fmnts/declarative-forms/common';
import { TranslationService } from '@fmnts/i18n';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';
import { patchState, signalState } from '@ngrx/signals';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
} from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';

/**
 * This is the value used if no item is selected
 */
const NoValue = none;
type FieldValue = Option<unknown>;

enum MoveCursorDirection {
  Up,
  Down,
}

function getMoveDirection(ev: KeyboardEvent): MoveCursorDirection | undefined {
  const { key } = ev;
  switch (key) {
    case 'ArrowUp':
      return MoveCursorDirection.Up;
    case 'ArrowDown':
      return MoveCursorDirection.Down;
    default:
      return undefined;
  }
}

type SelectState = {
  typeAheadValue: string;
  typeAheadReadonly: boolean;
  items: SelectInputItem[];
  availableItems: SelectInputItem[];
  dropdownShown: boolean;
  cursorIndex: number | undefined;
};

@Component({
  selector: 'next-select-field',
  templateUrl: './select-field.component.html',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule],
})
export class SelectFieldComponent
  extends AbstractDeclarativeInputFieldComponent<FormSelectField, FieldValue>
  implements OnInit
{
  private readonly translationService = inject(TranslationService);
  private readonly conditionalService = inject(ConditionalService);
  private readonly destroyRef = inject(DestroyRef);

  @HostBinding('class.form-field') formFieldClass = true;
  @HostBinding('class.form-field__select') componentFormFieldClass = true;

  @HostBinding('class.form-field__select--disabled')
  @Input()
  public override isReadonly = false;

  @ViewChild('list') listRef!: ElementRef<HTMLUListElement>;
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;

  selectState = signalState<SelectState>({
    typeAheadValue: '',
    typeAheadReadonly: false,
    items: [],
    availableItems: [],
    dropdownShown: false,
    /**
     * Stores the current position of the list item cursor
     * or undefined if the cursor is not available.
     */
    cursorIndex: undefined,
  });

  /** Current value of the field */
  protected _value: FieldValue = NoValue;
  get value(): FieldValue {
    return this._value;
  }

  /** Setter for the item */
  set item(item: SelectInputItem | undefined) {
    const value = item !== undefined ? item.value : NoValue;
    this.changeValue(value);
    this.updateSearchValue(item);
  }
  /**
   * Listens on typeahead value changes and needs to update the internal value to NoValue as it should show all the available items at first.
   * Once the dropdown is closed, the value will be updated to the selected item.
   **/
  onTypeAheadValueChange(value: string) {
    this._value = NoValue;
    patchState(this.selectState, { typeAheadValue: value });
  }

  protected readonly filteredItems = computed(() => {
    const typeAheadValue = this.selectState.typeAheadValue();
    const availableItems = this.selectState.availableItems() ?? [];
    return this.selectState.typeAheadReadonly() || this.value !== NoValue
      ? availableItems
      : this.filterItems(availableItems, typeAheadValue);
  });

  private _isClicking = false;
  private _mousePosition = {
    x: 0,
    y: 0,
  };

  /** @internal */
  protected readonly _iconOpen = faAngleDown;

  constructor() {
    super();

    toObservable(this.selectState.cursorIndex)
      .pipe(filter(isDefined), takeUntilDestroyed(this.destroyRef))
      .subscribe((position) => {
        const list = this.listRef.nativeElement;
        const { scrollTop, clientHeight, children } = list;
        const childNode = children.item(position) as HTMLLIElement | null;
        if (!childNode) {
          return;
        }
        const { offsetTop: itemTop, clientHeight: itemHeight } = childNode;
        const itemBottom = itemTop + itemHeight;

        if (itemTop < scrollTop) {
          // Scroll upwards if needed
          list.scrollTop = itemTop;
        } else if (scrollTop + clientHeight < itemBottom) {
          // Scroll downwards if needed
          list.scrollTop = itemBottom - clientHeight;
        }
      });
    toObservable(this.selectState.dropdownShown)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((dropdownShown) => {
        // Unset cursor index or reset when opening the dropdown for selected item
        patchState(this.selectState, {
          cursorIndex:
            dropdownShown && this._value !== NoValue
              ? this.filteredItems().findIndex(
                  bySelectItemValueEqeqeq(this._value),
                )
              : undefined,
        });
      });

    toObservable(this.selectState.availableItems)
      .pipe(
        map((availableItems, index) => ({
          availableItems,
          isFirstChange: index === 0,
        })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ availableItems, isFirstChange }) => {
        // On the first emit, we're dealing with the value passed via `writeValue`.
        // At the moment we explicitly don't want to enfore only available item values, because
        // this breaks self checkout functionality (which has selects with missing items).
        this._updateValueOnAvailableItems(availableItems, !isFirstChange);
      });
  }

  ngOnInit(): void {
    const items = this.getItemsFromField();
    patchState(this.selectState, {
      items: items ? items.filter((item) => this.hasTranslation(item)) : [],
    });

    if (!this.field) {
      patchState(this.selectState, {
        availableItems: [],
        typeAheadReadonly: true,
      });
    } else {
      const itemConditions =
        this.field.widgetOptions.itemConditions ?? undefined;
      patchState(this.selectState, {
        typeAheadReadonly: !(
          this.field.widgetOptions.hasTypeAhead ??
          {} ??
          true
        ),
      });
      const allItems = this.selectState.items();
      if (!itemConditions) {
        patchState(this.selectState, { availableItems: allItems });
      } else {
        this.form.flatValueChanges
          .pipe(
            debounceTime(100),
            startWith(this.form.flatValue),
            map((changes) =>
              allItems.filter((i) =>
                this.checkConditions(changes, itemConditions[i.value]),
              ),
            ),
            distinctUntilArrayItemChanged(
              (prev, current) => prev.value === current.value,
            ),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((updatedItems) => {
            patchState(this.selectState, { availableItems: updatedItems });
          });
      }
    }
  }

  updateSearchValue(item: SelectInputItem | undefined) {
    patchState(this.selectState, {
      typeAheadValue:
        item === undefined || item === NoValue
          ? ''
          : this.translationService.getInlineTranslation(item.label),
    });
  }

  updateCursorPosition(position: number) {
    if (this.selectState.cursorIndex() === position) {
      return;
    }
    patchState(this.selectState, { cursorIndex: position });
  }

  moveCursor(direction: MoveCursorDirection) {
    const current = this.selectState.cursorIndex();
    if (typeof current !== 'number') {
      this.updateCursorPosition(0);
      return;
    }

    const diff = direction === MoveCursorDirection.Up ? -1 : 1;
    const newPosition = current + diff;
    const wrappedPosition =
      newPosition < 0
        ? this.filteredItems().length - 1
        : newPosition >= this.filteredItems().length
          ? 0
          : newPosition;

    this.updateCursorPosition(wrappedPosition);
  }

  writeValue(value: unknown) {
    this._value = value;
    this.updateSearchValue(
      this.selectState.items().find(bySelectItemValueEqeqeq(value)),
    );
  }

  protected changeValue(value: unknown) {
    this._value = value;
    this._onChange(value);
  }

  openDropdown() {
    patchState(this.selectState, { dropdownShown: true });
  }

  /**
   * @param resetValue If true, the search value is reset
   */
  closeDropdown() {
    if (!this.selectState.dropdownShown()) {
      return;
    }

    this._onTouched();
    patchState(this.selectState, { dropdownShown: false });
    this.input.nativeElement.blur();
    if (this.selectState.typeAheadReadonly()) {
      return;
    }
    this.item = this.findItemByLabel(
      this.selectState.typeAheadValue(),
      this.selectState.items(),
    );
  }

  /**
   * Updates the value and type ahead value based on the current value and available items.
   *
   * @param availableItems Array of available items
   * @param unsetValueIfNotAvailable If `true`, the current value is changed if
   */
  private _updateValueOnAvailableItems(
    availableItems: SelectInputItem[],
    unsetValueIfNotAvailable: boolean,
  ) {
    /**
     * null means explicitly no value.
     * undefined means not found in available items.
     */
    const selectedItem =
      this._value === NoValue
        ? null
        : availableItems.find(
            // The backend might send mismatching default values and item values, so
            // we explicitly don't do a eqeqeq check here
            // eslint-disable-next-line eqeqeq
            ({ value }) => value == this._value,
          );

    // If the item is not available, we want to unset the field value.
    if (selectedItem === undefined && unsetValueIfNotAvailable) {
      this.changeValue(NoValue);
    }

    // Also update search value
    this.updateSearchValue(selectedItem ?? undefined);
  }

  protected filterItems(
    _availableItems: SelectInputItem[],
    searchValue: string,
  ) {
    const regexp = new RegExp(escapeRegExpString(searchValue), 'i');
    return _availableItems.filter(({ label }) =>
      regexp.test(this.translationService.getInlineTranslation(label)),
    );
  }

  protected findItemByLabel(
    label: string,
    items: SelectInputItem[],
  ): SelectInputItem | undefined {
    return items.find(
      (item) =>
        label === this.translationService.getInlineTranslation(item.label),
    );
  }

  protected getItemsFromField(): SelectInputItem[] | undefined {
    return this.field.widgetOptions.items;
  }

  onInputFocus(ev: FocusEvent, inputElement: HTMLInputElement) {
    this.openDropdown();

    if (!inputElement.readOnly) {
      inputElement.select();
    }
  }

  onContextMenu(event: MouseEvent) {
    // prevent the "right click" menu from opening when touching selected text
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  onInputBlur() {
    if (!this._isClicking) {
      this.item = this.findItemByLabel(
        this.selectState.typeAheadValue(),
        this.selectState.items(),
      );
      this.closeDropdown();
    }
  }

  onListItemMouseDown(item: SelectInputItem, event: MouseEvent) {
    this._isClicking = true;
    this._mousePosition.x = event.screenX;
    this._mousePosition.y = event.screenY;
    // Stop propagation to prevent any underlaying elements to gain focus
    event.stopPropagation();
  }

  onListItemTouchStart(item: SelectInputItem, event: TouchEvent) {
    this._isClicking = true;
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      this._mousePosition.x = touch.screenX;
      this._mousePosition.y = touch.screenY;
    }
    // Stop propagation to prevent any underlaying elements to gain focus
    event.stopPropagation();
  }

  onListItemMouseUp(item: SelectInputItem, event: MouseEvent) {
    this._isClicking = false;
    if (
      this._mousePosition.x &&
      this._mousePosition.y &&
      this._mousePosition.x === event.screenX &&
      this._mousePosition.y === event.screenY
    ) {
      this.item = item;
      this.closeDropdown();
      // Stop propagation to prevent any underlaying elements to gain focus
      event.stopPropagation();
      return false;
    }
  }

  onListItemTouchEnd(item: SelectInputItem, event: TouchEvent) {
    this._isClicking = false;
    if (event.changedTouches.length > 0) {
      const touch = event.changedTouches[0];
      if (
        this._mousePosition.x &&
        this._mousePosition.y &&
        this._mousePosition.x === touch.screenX &&
        this._mousePosition.y === touch.screenY
      ) {
        this.item = item;
        this.closeDropdown();
        // Stop propagation to prevent any underlaying elements to gain focus
        event.stopPropagation();
        return false;
      }
    }
  }

  onListItemClicked(item: SelectInputItem, event: MouseEvent) {
    // Stop propagation to prevent any underlaying elements to gain focus
    event.stopPropagation();
    return false;
  }

  onListMouseOut() {
    patchState(this.selectState, { cursorIndex: undefined });
  }

  onListMouseEnter() {
    patchState(this.selectState, { cursorIndex: undefined });
  }

  get showError(): boolean {
    return (
      this.form && (this.value || this.control.touched) && this.control.invalid
    );
  }

  get notFoundError() {
    return this.translationService.getTranslation(
      'field_select_not_found',
      this.selectState.typeAheadValue(),
    );
  }

  @HostListener('keydown', ['$event']) onKeyDown(ev: KeyboardEvent) {
    const { key } = ev;

    if ('Enter' === key || 'Tab' === key) {
      const cursorIndex = this.selectState.cursorIndex();
      const index =
        typeof cursorIndex === 'number'
          ? cursorIndex
          : this.filteredItems().length === 1
            ? 0
            : -1;
      if (index >= 0) {
        this.item = this.filteredItems()[index];
        this.closeDropdown();
      }
      return;
    }

    const moveDirection = getMoveDirection(ev);
    if (moveDirection !== undefined) {
      ev.preventDefault();
      this.moveCursor(moveDirection);
    }
  }

  /**
   * @param changes Changes to the form
   * @param condition Condition to be fulfilled
   *
   * @returns
   * `true`, if `condition` is fulfilled or no conditions are passed.
   * Otherwise `false`.
   */
  private checkConditions(
    changes: Record<string, unknown>,
    condition: IFieldCondition,
  ) {
    return !condition
      ? true
      : this.conditionalService.checkObj(changes, condition);
  }
  private hasTranslation(item: SelectInputItem): boolean {
    return this.translationService.getInlineTranslation(item.label) !== '';
  }
}
